import React, { useState } from "react";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { loadImageDimensions, urlToBase64 ,capitalizeFirstLetter, joinAddress, parseAmount} from "../modules/invoiceUtils";
import uRLs from "../../utils/baseUtils";
import { usePermission } from "../../context/PermissionContext";

export  const generateInvSevInvoicePdf = async (  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,) => {
  const {
    documentConfig,
    isInterState,
    isPreSale,   
    hasPayment
  } = invoiceConfig;
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    let y = PADDING + 5;

    // --- 1. HEADER SECTION ---
    // Logo (Top Left)
    if (documentConfig?.image) {
      try {
         const imagePath =documentConfig?.image;
        
        const base64 = await urlToBase64(imagePath);

        // 1. Get real dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 2. Define your maximum allowed size for the logo area
        const maxWidth = 30; // Slightly larger for better readability
        const maxHeight = 12;

        // 3. Calculate the ratio that fits without squeezing
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const finalWidth = width * ratio;
        const finalHeight = height * ratio;

        // 4. Center the image vertically in the 12-unit high 'maxHeight' space
        const adjustedY = y - 5 + (maxHeight - finalHeight) / 2;

        // 5. Add the image with dynamic dimensions
        doc.addImage(
          base64,
          "PNG",
          MARGIN_LEFT,
          adjustedY,
          finalWidth,
          finalHeight,
        );
      } catch (err) {
        // Fallback: If image fails, show company title
        console.warn("Logo failed to load:", err);
        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(14);
        doc.text(documentConfig?.fromData?.title || "COMPANY LOGO", MARGIN_LEFT, y);
      }
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20, 40, 100);
    doc.text(documentConfig?.title, MARGIN_RIGHT, y, { align: "right" });
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(`${documentConfig?.numberLabel} ${documentConfig?.number || ""}`, MARGIN_RIGHT, y, {
      align: "right",
    });

    // --- 2. SELLER DETAILS (Left) ---
    y += 6;
    doc.setFontSize(10);
    doc.setFont("Inter-Bold", "bold");
    doc.text(documentConfig?.fromData?.title || "", MARGIN_LEFT, y);

    y += 5;
    doc.setFontSize(8);
    doc.text(`GSTIN ${documentConfig?.fromData?.gstnumber || ""}`, MARGIN_LEFT, y);

    y += 4;
    doc.setFont("Inter-Regular", "normal");
    const sellerAddress = joinAddress(
      documentConfig?.fromData?.addressline1,
      documentConfig?.fromData?.addressline2,
      documentConfig?.fromData?.district,
      documentConfig?.fromData?.state,
      documentConfig?.fromData?.pincode,
    );
    const sellerAddrLines = doc.splitTextToSize(sellerAddress, 70);
    doc.text(sellerAddrLines, MARGIN_LEFT, y);

    y += sellerAddrLines.length * 4;
    const drawBoldLabelNormalValue = (label, value, x, currentY) => {
      // 1. Set to Bold
      doc.setFont("Inter-Regular", "normal");
      const labelText = `${label} `; // Removed colon if you want it exactly like the image
      doc.text(labelText, x, currentY);

      // 2. Measure the bold text width
      const labelWidth = doc.getTextWidth(labelText);

      // 3. Set to Normal
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(value || ""), x + labelWidth, currentY);
    };
    // drawBoldLabelNormalValue(
    //   "Mobile",
    //   documentConfig?.fromData?.phone,
    //   MARGIN_LEFT,
    //   y
    // );
     const phone = ` ${
      documentConfig?.fromData?.phone ? ` ${documentConfig.fromData.phone}` : ""
    }${
      documentConfig?.fromData?.additionalPhone
        ? `, ${documentConfig.fromData.additionalPhone}`
        : ""
    }`
    const email = documentConfig?.fromData?.email;
    y -= 1;
    if (phone || email) {
      let currentX = MARGIN_LEFT;
      const currentY = y;
      if (phone) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Mobile: ", currentX, currentY);

        currentX += doc.getTextWidth("Mobile: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(phone), currentX, currentY);

        currentX += doc.getTextWidth(String(phone));
      }
      if (phone && email) {
        doc.setFont("Inter-Regular", "normal");
        doc.text("  |  ", currentX, currentY);

        currentX += doc.getTextWidth("  |  ");
      }
      if (email) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Email: ", currentX, currentY);

        currentX += doc.getTextWidth("Email: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(email), currentX, currentY);
      }
    }
    if (documentConfig?.fromData?.pannumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "PAN",
        documentConfig?.fromData.pannumber,
        MARGIN_LEFT,
        y,
      );
    }

    if (documentConfig?.fromData?.fssai) {
      y += 4;
      drawBoldLabelNormalValue(
        "FSSAI",
        documentConfig?.fromData.fssai,
        MARGIN_LEFT,
        y,
      );
    }

    if (documentConfig?.fromData?.cinnumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "CIN",
        documentConfig?.fromData.cinnumber,
        MARGIN_LEFT,
        y,
      );
    }

    const hasCustomer = !!documentConfig?.toData;
    const hasDate = !!data?.saledate;
    const hasSupply = !!documentConfig?.toData?.state;

    let hasBillSection = hasCustomer || hasDate || hasSupply;
    const hasShipping =
      documentConfig?.toData?.shippingAddress &&
      (documentConfig?.toData?.shippingAddress?.addressline1 ||
        documentConfig?.toData?.shippingAddress?.addressline2);

    const shipColX = 100; // center column
    const rightColX = 160;
    if (hasBillSection) {
      y += 6;

      const col2X = 140;

      /* --- LEFT: BILL TO --- */
      if (hasCustomer) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text(documentConfig?.toLabel, MARGIN_LEFT, y);
        let leftY = y + 4;
        const nameLines = doc.splitTextToSize(
          documentConfig?.toData?.name || "",
          80,
        );
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(documentConfig?.toData?.name || "", MARGIN_LEFT, leftY);
        leftY += nameLines.length * 4;

        if (documentConfig?.toData?.gstnumber) {
          doc.text(`GSTIN: ${documentConfig?.toData.gstnumber}`, MARGIN_LEFT, leftY);
          leftY += 4;
        }

        // Address
        const custAddress = [
          documentConfig?.toData?.addressline1,
          documentConfig?.toData?.addressline2,
          documentConfig?.toData?.district,
          documentConfig?.toData?.state,
          documentConfig?.toData?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        // let leftY = y + 12;

        if (custAddress) {
          const lines = doc.splitTextToSize(custAddress, 80);
          doc.setFont("Inter-Regular", "normal");
          doc.text(lines, MARGIN_LEFT, leftY);
          leftY += lines.length * 4;
        }
        leftY = leftY + 0.5;
        // Contact
        if (documentConfig?.toData?.phone || documentConfig?.toData?.email) {
          const contact = `Ph: ${documentConfig?.toData?.phone || ""} ${
            documentConfig?.toData?.email
              ? "| Email: " + documentConfig?.toData.email
              : ""
          }`;
          doc.text(contact, MARGIN_LEFT, leftY);
          leftY += 4;
        }
        if (documentConfig?.toData?.referenceid) {
          doc.setFont("Inter-Regular", "normal");
          doc.text(
            `Reference No: ${documentConfig?.toData.referenceid}`,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (documentConfig?.toData?.pannumber) {
          drawBoldLabelNormalValue(
            "PAN",
            documentConfig?.toData.pannumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (documentConfig?.toData?.fssai) {
          drawBoldLabelNormalValue(
            "FSSAI",
            documentConfig?.toData.fssai,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (documentConfig?.toData?.cinnumber) {
          drawBoldLabelNormalValue(
            "CIN",
            documentConfig?.toData.cinnumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }
        var leftEndY = leftY;
      }
      /* --- CENTER: SHIPPING ADDRESS --- */
      let shipEndY = y;

      if (hasShipping) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Shipping Address:", shipColX, y);

        const shipAddress = [
          documentConfig?.toData?.shippingAddress?.addressline1,
          documentConfig?.toData?.shippingAddress?.addressline2,
          documentConfig?.toData?.shippingAddress?.district,
          documentConfig?.toData?.shippingAddress?.state,
          documentConfig?.toData?.shippingAddress?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let shipY = y + 4;

        if (shipAddress) {
          const shipLines = doc.splitTextToSize(shipAddress, 50);
          doc.setFont("Inter-Regular", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(shipLines, shipColX, shipY);

          shipY += shipLines.length * 4;
        }

        shipEndY = shipY;
      }
      /* --- RIGHT: DATE / TIME / SUPPLY --- */
      let rightY = y;

      if (hasDate) {
        doc.setFont("Inter-Bold", "bold");
        doc.text(documentConfig?.invDate ||"Date : ", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(
         documentConfig?.Date?.date,
          MARGIN_RIGHT,
          rightY,
          { align: "right" },
        );

        rightY += 4;

        doc.setFont("Inter-Bold", "bold");
        doc.text(documentConfig?.invTime ||"Time :", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        const invTime = documentConfig?.Date?.time;

        doc.text(invTime, MARGIN_RIGHT, rightY, { align: "right" });

        rightY += 4;
      }

      if (hasSupply) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Place of Supply:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(documentConfig?.toData.state, MARGIN_RIGHT, rightY, {
          align: "right",
        });

        rightY += 4;
      }

      /* --- FINAL HEIGHT FIX --- */
      const finalY = Math.max(leftEndY || y, shipEndY || y, rightY);

      y = finalY + 2;
    }
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { top: PADDING, left: PADDING, right: PADDING, bottom: PADDING }, // Balanced
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        textColor: [0, 0, 0],
      },
      headStyles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fillColor: false,
        textColor: "#000",
        //  textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
        lineWidth: { bottom: 0.5 },
        lineColor: selectedTheme?.templateTheme?.borderColor,
      },
      columnStyles: {
        0: { cellWidth: 13, halign: "center" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 25, halign: "right" },
        3: { cellWidth: 25, halign: "right" },
        4: { cellWidth: 20, halign: "center" },
        5: { cellWidth: 25, halign: "right" },
      },
      head: [["#", "Item", "MRP", "Selling Price", "Qty", "Amount"]],
      body: documentConfig?.items.map((it, i) => [
        i + 1,
        it.hsnsac
          ? `${it.title}\n${it.imode === "SERVICE" ? "SAC:" : "HSN:"} ${it.hsnsac}`
          : it.title,
        `${it.mrp}`,
        `${it.baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${it.lineGrandTotal}`,
      ]),
      didParseCell: function (data) {
        // Header alignment
         if (data.section === "head") {
          const colId = data.column.index;
        if ([0,1,2, 3,4,5].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }
        if (data.section === "body") {
          // Light gray divider lines
          data.cell.styles.lineWidth = { bottom: 0.1 };
        }
      },
    });

    y = doc.lastAutoTable.finalY + 5;

    if (y + PADDING > pageH - 20) {
      doc.addPage();
      y = PADDING + 5;
    }
    const footerTop = y;
    let leftY = footerTop;
    let rightY = footerTop;

    // --- 1. LEFT COLUMN: Bank Details ---
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    if (documentConfig?.bankDetails) {
      doc.text("Bank Details:", MARGIN_LEFT, leftY);
      leftY += 5;

      const bankInfo = [
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],
      ["UPI ID:", documentConfig?.upiId || ""],
    ].filter(([_, value]) => Boolean(value));

      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.text(label, MARGIN_LEFT, leftY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(String(value || ""), MARGIN_LEFT + 25, leftY);
        leftY += 4;
      });
    }
    const upiId = storeSettings?.paymentSettings?.upiId || "";
    doc.setFontSize(8);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
      documentConfig?.bankDetails?.beneficiaryName || "",
    )}&cu=INR`;
    const qrX = pageW / 2 - 15;
    if (qrCodeDataUrl) {
      if (upiId && upiId.trim() !== "") {
        const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
          errorCorrectionLevel: "L",
          scale: 4,
          margin: 1,
        });
        doc.addImage(qrCodeDataUrl, "JPEG", qrX, footerTop - 3, 20, 20);
      }
    }
    const summaryX = 140;

  
    // Rows 2-4: Taxable & GST (Bold Font)
    const summaryItems = [["Taxable Amount", `${documentConfig?.totalBasePrice}`]];
    if (isInterState) {
      summaryItems.push(["IGST ", `${documentConfig?.igstAmount}`]);
    } else {
      summaryItems.push(["CGST ", `${documentConfig?.cgstAmount}`]);
      summaryItems.push(["SGST ", `${documentConfig?.sgstAmount}`]);
    }

    doc.setFontSize(9);
    summaryItems.forEach(([label, value]) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setTextColor(0);
      doc.text(label, summaryX, rightY);
      doc.text(value, MARGIN_RIGHT, rightY, { align: "right" });
      rightY += 5;
    });
    rightY += 2;
    // --- 4. THE TOTAL BOX ---
    const internalPadding = 2; // Adjust this value to get the exact gap you want
    doc.setFillColor(selectedTheme?.templateTheme?.primaryColor);
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setTextColor(selectedTheme?.templateTheme?.foregroundColor);
    doc.setLineWidth(0.4);
    doc.rect(summaryX - 2, rightY - 4, MARGIN_RIGHT - summaryX + 2, 8, "FD");
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(11);
    doc.text("Total", summaryX, rightY + 1.5);
    doc.text(
      `${documentConfig?.grandTotal}`,
      MARGIN_RIGHT - internalPadding, // This moves the amount away from the line
      rightY + 1.5,
      { align: "right" },
    );
    // doc.text(formatCurrency((CurrencyConverter.toCurrency(data.grandtotal|| 0, "INR")), "INR", true, true), MARGIN_RIGHT, rightY + 1.5, { align: "right" });
    rightY += 8;
    if (leftY > pageH - 20) {
      doc.addPage();
      leftY = PADDING + 5;
      rightY = PADDING + 5;
    }
   
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text("Amount Payable:", summaryX, rightY);
    doc.text(`${data?.balanceDue}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    rightY += 5;
    doc.setFont("Inter-Regular", "normal");
    doc.text("Amount Paid:", summaryX, rightY);
    doc.text(documentConfig?.paidAmount, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const amountWords = `Total amount (in words) ${
      documentConfig?.amountInWords || ""
    }`;
    // doc.text(amountWords, MARGIN_LEFT, wordsY);
    const wordLines = doc.splitTextToSize(amountWords, qrX + 25);
    doc.text(wordLines, MARGIN_LEFT, leftY + 2);

    leftY += wordLines.length * 4 + 3;

    let paymentMethodsHeight = 0;
    if (hasPayment) {
      if (parseAmount(documentConfig?.cashPaid) > 0) paymentMethodsHeight += 4;
      if (parseAmount(documentConfig?.upiPaid) > 0) paymentMethodsHeight += 4;
      if (parseAmount(documentConfig?.cardPaid) > 0) paymentMethodsHeight += 4;
      if (parseAmount(documentConfig?.onlinePaid) > 0) paymentMethodsHeight += 4;
    }
    const totalNeededHeight = paymentMethodsHeight + wordLines.length * 4 + 10;
    if (leftY + totalNeededHeight > pageH - 20) {
      doc.addPage();
      leftY = PADDING + 5;
      rightY = PADDING + 5;
    }
    const payments = [
      { amount: documentConfig?.cashPaid, label: "Cash" },
      { amount: documentConfig?.upiPaid, label: "UPI" },
      { amount: documentConfig?.cardPaid, label: "Card" },
      { amount: documentConfig?.onlinePaid, label: "Online" },
    ].filter((p) =>Math.abs(parseAmount(p.amount)) > 0);

    const drawPayment = (amount, label, isLast) => {
        const parsedAmount = parseAmount(amount);

      if (Math.abs(parsedAmount) > 0) {
        let x = MARGIN_LEFT;

        // 👉 Bold Amount
        doc.setFont("Inter-Bold", "bold");
        const amountText = `${amount} `;
        doc.text(amountText, x, leftY);

        x += doc.getTextWidth(amountText);

        // 👉 Normal Text
        doc.setFont("Inter-Regular", "normal");
        doc.text(`Paid via ${label}`, x, leftY);

        // 👉 ONLY add spacing if NOT last
        if (!isLast) {
          leftY += 4;
        }
      }
    };
    if (hasPayment) {
      payments.forEach((p, index) => {
        const isLast = index === payments.length - 1;
        drawPayment(p.amount, p.label, isLast);
      });
    }
    doc.setFont("Inter-Regular", "normal");

    if (hasPayment) {
      y = Math.max(leftY, rightY) + 8;
    } else {
      y = Math.max(leftY, rightY + 10);
    }
    if (y > pageH - 40) {
      doc.addPage();
      y = PADDING + 5;
    }

    let rowStartY = y;
    if (documentConfig?.remarks) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      if (rowStartY > pageH - 30) {
        doc.addPage();
        rowStartY = PADDING;
      }

      doc.text("Remark", PADDING, rowStartY);

      const innerPaddingX = 2;

      // Kept your text width constraint (Note: removing +25 ensures text doesn't overflow the physical box boundary)
      const maxTextWidth = pageW - PADDING * 2 - innerPaddingX * 2 + 25;

      const remarkLines = doc.splitTextToSize(
        capitalizeFirstLetter(documentConfig?.remarks),
        maxTextWidth,
      );

      // Tightened Line Height and Padding
      const textLineHeight = 4.0;
      const boxPaddingTop = 2.5; // Reduced top padding
      const boxPaddingBottom = 2.5; // Reduced bottom padding

      // Calculate exact container height
      const remarkHeight =
        boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;
      const boxStartY = rowStartY + 2.5;

      // Draw the tight rounded container box
      doc.setDrawColor(180);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);
      doc.setTextColor(0);

      // Tight baseline: places the first line's baseline perfectly within the box padding
      let remarkY = boxStartY + boxPaddingTop;

      remarkLines.forEach((line) => {
        if (remarkY > pageH - PADDING) {
          doc.addPage();
          remarkY = PADDING + 6;
        }

        doc.text(line, PADDING, remarkY);
        remarkY += textLineHeight;
      });

      // Update rowStartY safely below the tight box
      rowStartY = boxStartY + remarkHeight + 2;
    }
    if (terms?.length > 0  && !isPreSale) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      // Page break check
      if (rowStartY > pageH - 25) {
        doc.addPage();
        rowStartY = PADDING + 5;
      }

      doc.text("Terms & Conditions", PADDING, rowStartY);
      doc.setTextColor(0);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW - 50); // Limit width to half page
      let currentTermY = rowStartY + 5;

      termLines.forEach((line) => {
        if (currentTermY > pageH - 20) {
          doc.addPage();
          currentTermY = 10;
        }
        doc.text(line, PADDING, currentTermY);
        currentTermY += 5;
      });

      // Update global y based on how long the terms were
      y = currentTermY;
    }

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 35;
        const maxHeight = 20;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          MARGIN_RIGHT - imgW,
          rowStartY, // Start at the same Y as the Terms header
          imgW,
          imgH,
        );
      } catch {
        console.warn("Signature load failed");
      }
    }
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(10);
    doc.text("Authorized Signatory", MARGIN_RIGHT, rowStartY + 13, {
      align: "right",
    });
    if (storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW / 2 - 10;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");

      // Start X so whole string is right-aligned
      const startX = rightX;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y, {
        align: "center",
      });

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + 1 + normalW, y, {
        align: "center",
      });
    }
    // setDownload(false);
    return doc;
  };

 export const generateInvSevBillingPdf = async ( data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,) => {
   const {
    documentConfig,    
    isPreSale,  
    hasPayment
  } = invoiceConfig;
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    let y = PADDING + 5;

    // --- 1. HEADER SECTION ---
    // Logo (Top Left)
    if (documentConfig?.image) {
      try {
         const imagePath =documentConfig?.image;
        
        const base64 = await urlToBase64(imagePath);

        // 1. Get real dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 2. Define your maximum allowed size for the logo area
        const maxWidth = 30; // Slightly larger for better readability
        const maxHeight = 12;

        // 3. Calculate the ratio that fits without squeezing
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const finalWidth = width * ratio;
        const finalHeight = height * ratio;

        // 4. Center the image vertically in the 12-unit high 'maxHeight' space
        const adjustedY = y - 5 + (maxHeight - finalHeight) / 2;

        // 5. Add the image with dynamic dimensions
        doc.addImage(
          base64,
          "PNG",
          MARGIN_LEFT,
          adjustedY,
          finalWidth,
          finalHeight,
        );
      } catch (err) {
        // Fallback: If image fails, show company title
        console.warn("Logo failed to load:", err);
        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(14);
        doc.text(documentConfig?.fromData?.title || "COMPANY LOGO", MARGIN_LEFT, y);
      }
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20, 40, 100);
    doc.text(documentConfig?.title, MARGIN_RIGHT, y, { align: "right" });
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(`${documentConfig?.numberLabel} ${documentConfig?.number || ""}`, MARGIN_RIGHT, y, {
      align: "right",
    });

    // --- 2. SELLER DETAILS (Left) ---
    y += 6;
    doc.setFontSize(10);
    doc.setFont("Inter-Bold", "bold");
    doc.text(documentConfig?.fromData?.title || "", MARGIN_LEFT, y);

    y += 5;
    doc.setFontSize(8);
    doc.text(`GSTIN ${documentConfig?.fromData?.gstnumber || ""}`, MARGIN_LEFT, y);

    y += 4;
    doc.setFont("Inter-Regular", "normal");
    const sellerAddress = joinAddress(
      documentConfig?.fromData?.addressline1,
      documentConfig?.fromData?.addressline2,
      documentConfig?.fromData?.district,
      documentConfig?.fromData?.state,
      documentConfig?.fromData?.pincode,
    );
    const sellerAddrLines = doc.splitTextToSize(sellerAddress, 70);
    doc.text(sellerAddrLines, MARGIN_LEFT, y);

    y += sellerAddrLines.length * 4;
    const drawBoldLabelNormalValue = (label, value, x, currentY) => {
      // 1. Set to Bold
      doc.setFont("Regular", "normal");;
      const labelText = `${label} `; // Removed colon if you want it exactly like the image
      doc.text(labelText, x, currentY);

      // 2. Measure the bold text width
      const labelWidth = doc.getTextWidth(labelText);

      // 3. Set to Normal
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(value || ""), x + labelWidth, currentY);
    };
    // drawBoldLabelNormalValue(
    //   "Mobile",
    //   documentConfig?.fromData?.phone,
    //   MARGIN_LEFT,
    //   y
    // );
    const phone = ` ${
      documentConfig?.fromData?.phone ? ` ${documentConfig.fromData.phone}` : ""
    }${
      documentConfig?.fromData?.additionalPhone
        ? `, ${documentConfig.fromData.additionalPhone}`
        : ""
    }`
    const email = documentConfig?.fromData?.email;
    y -= 1;
    if (phone || email) {
      let currentX = MARGIN_LEFT;
      const currentY = y;
      if (phone) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Mobile: ", currentX, currentY);

        currentX += doc.getTextWidth("Mobile: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(phone), currentX, currentY);

        currentX += doc.getTextWidth(String(phone));
      }
      if (phone && email) {
        doc.setFont("Inter-Regular", "normal");
        doc.text("  |  ", currentX, currentY);

        currentX += doc.getTextWidth("  |  ");
      }
      if (email) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Email: ", currentX, currentY);

        currentX += doc.getTextWidth("Email: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(email), currentX, currentY);
      }
    }
    if (documentConfig?.fromData?.pannumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "PAN:",
        documentConfig?.fromData.pannumber,
        MARGIN_LEFT,
        y,
      );
    }

    if (documentConfig?.fromData?.fssai) {
      y += 4;
      drawBoldLabelNormalValue(
        "FSSAI:",
        documentConfig?.fromData.fssai,
        MARGIN_LEFT,
        y,
      );
    }

    if (documentConfig?.fromData?.cinnumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "CIN:",
        documentConfig?.fromData.cinnumber,
        MARGIN_LEFT,
        y,
      );
    }

    // --- 3. BILL TO & DATES ---
    const hasCustomer = !!documentConfig?.toData;
    const hasDate = !!data?.saledate;
    const hasSupply = !!documentConfig?.toData?.state;

    let hasBillSection = hasCustomer || hasDate || hasSupply;
    const hasShipping =
      documentConfig?.toData?.shippingAddress &&
      (documentConfig?.toData?.shippingAddress?.addressline1 ||
        documentConfig?.toData?.shippingAddress?.addressline2);

    const shipColX = 100; // center column
    const rightColX = 160;
    if (hasBillSection) {
      y += 6;

      const col2X = 140;

      /* --- LEFT: BILL TO --- */
      if (hasCustomer) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text(documentConfig?.toLabel, MARGIN_LEFT, y);

        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(documentConfig?.toData?.name || "", MARGIN_LEFT, y + 4);

        if (documentConfig?.toData?.gstnumber) {
          doc.text(`GSTIN: ${documentConfig?.toData.gstnumber}`, MARGIN_LEFT, y + 8);
        }

        // Address
        const custAddress = [
          documentConfig?.toData?.addressline1,
          documentConfig?.toData?.addressline2,
          documentConfig?.toData?.district,
          documentConfig?.toData?.state,
          documentConfig?.toData?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let leftY = y + 12;

        if (custAddress) {
          const lines = doc.splitTextToSize(custAddress, 80);
          doc.setFont("Inter-Regular", "normal");
          doc.text(lines, MARGIN_LEFT, leftY);
          leftY += lines.length * 4;
        }
        leftY -= 1;
        // Contact
        if (documentConfig?.toData?.phone || documentConfig?.toData?.email) {
          const contact = `Ph: ${documentConfig?.toData?.phone || ""} ${
            documentConfig?.toData?.email
              ? "| Email: " + documentConfig?.toData.email
              : ""
          }`;
          doc.text(contact, MARGIN_LEFT, leftY);
          leftY += 4;
        }
        if (documentConfig?.toData?.referenceid) {
          doc.setFont("Regular", "normal");
          doc.text(
            `Reference No: ${documentConfig?.toData.referenceid}`,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }
        if (documentConfig?.toData?.pannumber) {
          drawBoldLabelNormalValue(
            "PAN",
            documentConfig?.toData.pannumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (documentConfig?.toData?.fssai) {
          drawBoldLabelNormalValue(
            "FSSAI",
            documentConfig?.toData.fssai,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (documentConfig?.toData?.cinnumber) {
          drawBoldLabelNormalValue(
            "CIN",
            documentConfig?.toData.cinnumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }
        var leftEndY = leftY;
      }
      /* --- CENTER: SHIPPING ADDRESS --- */
      let shipEndY = y;

      if (hasShipping) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Shipping Address:", shipColX, y);

        const shipAddress = [
          documentConfig?.toData?.shippingAddress?.addressline1,
          documentConfig?.toData?.shippingAddress?.addressline2,
          documentConfig?.toData?.shippingAddress?.district,
          documentConfig?.toData?.shippingAddress?.state,
          documentConfig?.toData?.shippingAddress?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let shipY = y + 4;

        if (shipAddress) {
          const shipLines = doc.splitTextToSize(shipAddress, 50);
          doc.setFont("Inter-Regular", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(shipLines, shipColX, shipY);

          shipY += shipLines.length * 4;
        }

        shipEndY = shipY;
      }
      /* --- RIGHT: DATE / TIME / SUPPLY --- */
      let rightY = y;

      if (hasDate) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Bill Date:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(
          documentConfig?.Date?.date,
          MARGIN_RIGHT,
          rightY,
          { align: "right" },
        );

        rightY += 4;

        doc.setFont("Inter-Bold", "bold");
        doc.text("Bill Time:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        const invTime = documentConfig?.Date?.time;

        doc.text(invTime, MARGIN_RIGHT, rightY, { align: "right" });

        rightY += 4;
      }

      if (hasSupply) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Place of Supply:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(documentConfig?.toData.state, MARGIN_RIGHT, rightY, {
          align: "right",
        });

        rightY += 4;
      }

      /* --- FINAL HEIGHT FIX --- */
      const finalY = Math.max(leftEndY || y, shipEndY || y, rightY);

      y = finalY + 2;
    }
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { top: PADDING, left: PADDING, right: PADDING, bottom: PADDING }, // Balanced
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        textColor: [0, 0, 0],
      },
      headStyles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fillColor: false,
        textColor: "#000",
        //  textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
        lineWidth: { bottom: 0.5 },
        lineColor: selectedTheme?.templateTheme?.borderColor,
      },
      columnStyles: {
        0: { cellWidth: 13, halign: "center" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 25, halign: "right" },
        3: { cellWidth: 20, halign: "center" },
        4: { cellWidth: 25, halign: "right" },
      },
      head: [["#", "Item", "Selling Price", "Qty", "Amount"]],
      body: documentConfig?.items.map((it, i) => [
        i + 1,
        it.hsnsac
          ? `${it.title}\n${it.imode === "SERVICE" ? "SAC:" : "HSN:"} ${it.hsnsac}`
          : it.title,
        `${it.baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode  || "";
        })()}`,
        `${it.lineGrandTotal}`,
      ]),
      didParseCell: function (data) {
        // Header alignment
         if (data.section === "head") {
          const colId = data.column.index;
        if ([0,1,2, 3,4].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }
        if (data.section === "body") {
          // Light gray divider lines
          data.cell.styles.lineWidth = { bottom: 0.1 };
        }
      },
    });

    y = doc.lastAutoTable.finalY + 5;
    if (y + PADDING > pageH - 20) {
      doc.addPage();
      y = PADDING + 5;
    }
    const footerTop = y;

    let leftY = footerTop;
    let rightY = footerTop;

    // --- 1. LEFT COLUMN: Bank Details ---
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    if (documentConfig?.bankDetails) {
      doc.text("Bank Details:", MARGIN_LEFT, leftY);
      leftY += 5;

      const bankInfo = [
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],
      ["UPI ID:", documentConfig?.upiId || ""],
    ].filter(([_, value]) => Boolean(value));

      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.text(label, MARGIN_LEFT, leftY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(String(value || ""), MARGIN_LEFT + 25, leftY);
        leftY += 4;
      });
    }

    const upiId = storeSettings?.paymentSettings?.upiId || "";
    doc.setFontSize(8);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
      documentConfig?.bankDetails?.beneficiaryName || "",
    )}&cu=INR`;
    const qrX = pageW / 2 - 15;
    if (qrCodeDataUrl) {
      if (upiId && upiId.trim() !== "") {
        const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
          errorCorrectionLevel: "L",
          scale: 4,
          margin: 1,
        });
        doc.addImage(qrCodeDataUrl, "JPEG", qrX, footerTop - 3, 20, 20);
      }
    }
    const summaryX = 140;
    // Rows 2-4: Taxable & GST (Bold Font)
    const summaryItems = [["Taxable Amount", `${documentConfig?.totalBasePrice}`]];

    doc.setFontSize(9);
    summaryItems.forEach(([label, value]) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setTextColor(0);
      doc.text(label, summaryX, rightY);
      doc.text(value, MARGIN_RIGHT, rightY, { align: "right" });
      rightY += 5;
    });
    rightY += 2;
    // --- 4. THE TOTAL BOX ---
    doc.setFillColor(selectedTheme?.templateTheme?.primaryColor);
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setTextColor(selectedTheme?.templateTheme?.foregroundColor);
    doc.setLineWidth(0.4);
    doc.rect(summaryX - 2, rightY - 4, MARGIN_RIGHT - summaryX + 2, 8, "FD");

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(11);
    doc.text("Total", summaryX, rightY + 1.5);
    doc.text(
      `${documentConfig?.grandTotal}`,
      MARGIN_RIGHT - 2, // This moves the amount away from the line
      rightY + 1.5,
      { align: "right" },
    );
    rightY += 8;
 
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text("Amount Payable:", summaryX, rightY);
    doc.text(`${data?.balanceDue}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    rightY += 5;
    doc.setFont("Inter-Regular", "normal");
    doc.text("Amount Paid:", summaryX, rightY);
    doc.text(documentConfig?.paidAmount, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const amountWords = `Total amount (in words) ${
      documentConfig?.amountInWords || ""
    }`;
    // doc.text(amountWords, MARGIN_LEFT, wordsY);
    const wordLines = doc.splitTextToSize(amountWords, qrX + 25);
    doc.text(wordLines, MARGIN_LEFT, leftY + 2);

    leftY += wordLines.length * 4 + 3;

    let paymentMethodsHeight = 0;
    if (hasPayment) {
      if (parseAmount(documentConfig?.cashPaid) > 0) paymentMethodsHeight += 4;
      if (parseAmount(documentConfig?.upiPaid) > 0) paymentMethodsHeight += 4;
      if (parseAmount(documentConfig?.cardPaid) > 0) paymentMethodsHeight += 4;
      if (parseAmount(documentConfig?.onlinePaid) > 0) paymentMethodsHeight += 4;

      const totalNeededHeight =
        paymentMethodsHeight + wordLines.length * 4 + 10;
      if (leftY + totalNeededHeight > pageH - 20) {
        doc.addPage();
        leftY = PADDING + 5;
        rightY = PADDING + 5;
      }
    }
    const payments = [
      { amount: documentConfig?.cashPaid, label: "Cash" },
      { amount: documentConfig?.upiPaid, label: "UPI" },
      { amount: documentConfig?.cardPaid, label: "Card" },
      { amount: documentConfig?.onlinePaid, label: "Online" },
    ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);

    const drawPayment = (amount, label, isLast) => {
  const parsedAmount = parseAmount(amount);

  if (Math.abs(parsedAmount) > 0) {
    let x = MARGIN_LEFT;

    doc.setFont("Inter-Bold", "bold");

    const amountText = `${amount} `;
    doc.text(amountText, x, leftY);

    x += doc.getTextWidth(amountText);

    doc.setFont("Inter-Regular", "normal");
    doc.text(`Paid via ${label}`, x, leftY);

    if (!isLast) {
      leftY += 4;
    }
  }
};
    if (hasPayment) {
      payments.forEach((p, index) => {
        const isLast = index === payments.length - 1;
        drawPayment(p.amount, p.label, isLast);
      });
    }

    doc.setFont("Inter-Regular", "normal");

    if (hasPayment) {
      y = Math.max(leftY, rightY) + 8;
    } else {
      y = Math.max(leftY, rightY + 10);
    }
    if (y > pageH - 40) {
      doc.addPage();
      y = PADDING + 5;
    }
    let rowStartY = y;
    if (documentConfig?.remarks) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      if (rowStartY > pageH - 30) {
        doc.addPage();
        rowStartY = PADDING;
      }

      doc.text("Remark", PADDING, rowStartY);

      const innerPaddingX = 2;

      // Kept your text width constraint (Note: removing +25 ensures text doesn't overflow the physical box boundary)
      const maxTextWidth = pageW - PADDING * 2 - innerPaddingX * 2 + 25;

      const remarkLines = doc.splitTextToSize(
        capitalizeFirstLetter(documentConfig?.remarks),
        maxTextWidth,
      );

      // Tightened Line Height and Padding
      const textLineHeight = 4.0;
      const boxPaddingTop = 2.5; // Reduced top padding
      const boxPaddingBottom = 2.5; // Reduced bottom padding

      // Calculate exact container height
      const remarkHeight =
        boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;
      const boxStartY = rowStartY + 2.5;

      // Draw the tight rounded container box
      doc.setDrawColor(180);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);
      doc.setTextColor(0);

      // Tight baseline: places the first line's baseline perfectly within the box padding
      let remarkY = boxStartY + boxPaddingTop;

      remarkLines.forEach((line) => {
        if (remarkY > pageH - PADDING) {
          doc.addPage();
          remarkY = PADDING + 6;
        }

        doc.text(line, PADDING, remarkY);
        remarkY += textLineHeight;
      });

      // Update rowStartY safely below the tight box
      rowStartY = boxStartY + remarkHeight + 2;
    }
    if (terms?.length > 0  && !isPreSale) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      // Page break check
      if (rowStartY > pageH - 25) {
        doc.addPage();
        rowStartY = PADDING + 5;
      }

      doc.text("Terms & Conditions", PADDING, rowStartY);
      doc.setTextColor(0);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW - 50); // Limit width to half page
      let currentTermY = rowStartY + 5;

      termLines.forEach((line) => {
        if (currentTermY > pageH - 20) {
          doc.addPage();
          currentTermY = 10;
        }
        doc.text(line, PADDING, currentTermY);
        currentTermY += 5;
      });

      // Update global y based on how long the terms were
      y = currentTermY;
    }

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 35;
        const maxHeight = 20;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          MARGIN_RIGHT - imgW,
          rowStartY, // Start at the same Y as the Terms header
          imgW,
          imgH,
        );
      } catch {
        console.warn("Signature load failed");
      }
    }
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(10);
    doc.text("Authorized Signatory", MARGIN_RIGHT, rowStartY + 13, {
      align: "right",
    });
    if (storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW / 2 - 10;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");

      // Start X so whole string is right-aligned
      const startX = rightX;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y, {
        align: "center",
      });

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + 1 + normalW, y, {
        align: "center",
      });
    }
    // setDownload(false);
    return doc;
  };

const ServiceInvoice = ({
   globalData,
  terms,
  qrCodeDataUrl,  
  selectedTheme,
  invoiceConfig,
}) => {
  const { documentConfig, showTaxDetails,hasPayment,isInterState, showBillDetails, isPreSale,  } =
    invoiceConfig;

  const { storeSettings } = usePermission();

  return (
    <>
                 {showTaxDetails ? (
              <>
                                 <div className="tm_container">
                    <div className="tm_invoice_wrap">
                      <div className="tm_invoice tm_style1 tm_type1">
                        <div className="tm_invoice_in p-2">
                          <div className="d-flex justify-content-between mb-2">
                            <div className="tm_logo text-start">
                              {documentConfig?.image && (
                                <img
                                  src={
                                    documentConfig?.image
                                  }
                                  className="img-rounded logo mb-0"
                                  height="45"
                                  width="150"
                                  style={{ objectFit: "contain" }}
                                />
                              )}
                            </div>
                            <div className="text-end">
                              <div className="font-16">
                                {documentConfig.title && (
                              <>{documentConfig.title}</>
                            )}
                                <div>
                                  <span className="font-12 fw-600">
                                     {documentConfig?.numberLabel}{" "}
                                  </span>
                                  {""}
                                  <strong className="font-12">
                                   {documentConfig?.number}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-6 pe-1 ">
                              <div className="d-flex gap-2 align-items-start">
                                <div>
                                  <div className="font-14 mb- fw-bold">
                                    {documentConfig?.fromData?.title || ""}
                                  </div>
                                  {documentConfig?.fromData?.gstnumber && (
                                    <div className="font-11 fw-600 mb-0 ">
                                      <>GSTIN:</>{" "}
                                      {documentConfig?.fromData?.gstnumber || ""}
                                    </div>
                                  )}
                                  <div className="font-11">
                                    {documentConfig?.fromData?.addressline1 || ""}{" "}
                                    {documentConfig?.fromData?.addressline2 || ""}{" "}
                                    {documentConfig?.fromData?.district || ""}
                                    {", "}
                                    {documentConfig?.fromData?.state || ""}
                                    {", "}
                                    {documentConfig?.fromData?.pincode || ""}
                                  </div>
                                  <div className="font-11">
                                    <strong>Mobile:</strong>{" "}
                                    {documentConfig?.fromData?.phone || ""}
                                  {documentConfig?.fromData?.additionalPhone && (
                                    <>, {documentConfig.fromData.additionalPhone}</>
                                  )} |{" "}
                                    <strong>Email:</strong>{" "}
                                    {documentConfig?.fromData?.email || "-"}
                                  </div>
                                  {documentConfig?.fromData?.pannumber && (
                                    <div className="font-11">
                                     PAN:{" "}
                                      {documentConfig?.fromData?.pannumber || ""}
                                    </div>
                                  )}
                                  {documentConfig?.fromData?.fssai && (
                                    <div className="font-11">
                                     FASSI:{" "}
                                      {documentConfig?.fromData?.fssai || ""}
                                    </div>
                                  )}
                                  {documentConfig?.fromData?.cinnumber && (
                                    <div className="font-11">
                                     CIN:{" "}
                                      {documentConfig?.fromData?.cinnumber}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row mt-2">
                            <div className="col-4 pe-1">
                              {documentConfig?.toData && (
                                  <>
                                    <div>
                                      <div>
                                        <span className="fw-500">{documentConfig?.toLabel}</span>
                                        {""}
                                      </div>
                                      <div className="font-12 mb-0">
                                        <strong>
                                          {documentConfig?.toData?.name || ""}
                                        </strong>
                                      </div>
                                      {documentConfig?.toData?.gstnumber && (
                                        <div className="font-11">
                                          <strong>GSTIN:</strong>{" "}
                                          {documentConfig?.toData?.gstnumber || ""}
                                        </div>
                                      )}
                                      <div className="font-11">
                                        {documentConfig?.toData?.addressline1 || ""}{" "}
                                        {documentConfig?.toData?.addressline2 ||
                                          ""}{" "}
                                      </div>
                                      <div className="font-11">
                                        {documentConfig?.toData?.state || ""}{" "}
                                        {documentConfig?.toData?.district || ""}{" "}
                                        {documentConfig?.toData?.country || ""}{" "}
                                        {documentConfig?.toData?.pincode || ""}
                                      </div>

                                      {documentConfig?.toData?.phone && (
                                        <div className="font-11">
                                          {" "}
                                          Ph: {documentConfig?.toData?.phone ||
                                            ""}{" "}
                                          | Email:{" "}
                                          {documentConfig?.toData?.email || ""}
                                        </div>
                                      )}
                                      {/* <div> Email {documentConfig?.toData?.email || ""}</div> */}
                                      {documentConfig?.toData?.referenceid && (
                                        <div className="font-11">
                                          Reference No:{" "}
                                          {documentConfig?.toData?.referenceid || ""}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.pannumber && (
                                        <div className="font-11">
                                          PAN:{" "}
                                          {documentConfig?.toData?.pannumber || ""}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.fssai && (
                                        <div className="font-11">
                                          FASSI:{" "}
                                          {documentConfig?.toData?.fssai || ""}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.cinnumber && (
                                        <div className="font-11">
                                          CIN :
                                          {documentConfig?.toData?.cinnumber || ""}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                            </div>
                            {documentConfig?.toData?.shippingAddress &&
                              (documentConfig?.toData?.shippingAddress
                                ?.addressline1 ||
                                documentConfig?.toData?.shippingAddress
                                  ?.addressline2) && (
                                <>
                                  <div className="col-5 pe-1 ms-auto">
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        // fontWeight: "600",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Shipping Address
                                    </div>
                                    <div>
                                      {documentConfig?.toData?.shippingAddress
                                        ?.addressline1 || ""}{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.addressline2 || ""}{" "}
                                    </div>
                                    <div>
                                      {documentConfig?.toData?.shippingAddress
                                        ?.state || ""}{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.district || ""}{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.country || ""}{" "}
                                      -{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.pincode || ""}
                                    </div>
                                  </div>
                                </>
                              )}
                            <div className="col-3 ps-1 ms-auto">
                              {documentConfig?.Date?.date && (
                                <>
                                  {/* justify-content-between pushes the value to the right end */}
                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">{documentConfig?.invDate ||"Date : "}</span>
                                    <strong className="fw-semibold">
                                      {documentConfig?.Date?.date}
                                    </strong>
                                  </div>

                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">{documentConfig?.invTime ||"Time :"}</span>
                                    <strong className="fw-semibold">
                                      {
                                     documentConfig?.Date?.time
                                      }
                                    </strong>
                                  </div>
                                </>
                              )}
                              {documentConfig?.toData?.state && (
                                <>
                                  <div className="d-flex  font-11 justify-content-between mb-10">
                                    <span className="fw-bold">
                                      Place of Supply:
                                    </span>
                                    <strong className="fw-semibold text-end ">
                                      {documentConfig?.toData?.state || ""}
                                    </strong>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <table
                            // className="items"
                            width="100%"
                            style={{
                              marginTop: "2px",
                              borderCollapse: "collapse",
                              // borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              // borderBottom: "1px solid #000"
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr
                              //  style={{
                              //   borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              //   borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`
                              // }}
                              >
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "20px",
                                    textAlign: "center",
                                  }}
                                >
                                  #
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    // width: "240px",
                                    textAlign: "center",
                                  }}
                                >
                                  Item
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "50px",
                                    textAlign: "center",
                                  }}
                                >
                                  MRP
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                  }}
                                >
                                  Selling Price
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                  }}
                                >
                                  Qty
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                  }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {documentConfig?.items?.map((product, index) => (
                                <tr
                                  className="align-top font-11"
                                  key={index}
                                  style={{
                                    borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <td className="force-style align-left">
                                    {index + 1}
                                  </td>
                                  <td className="force-style align-left">
                                    <div className="font-11">
                                      {product.title}
                                    </div>

                                    {product.hsnsac && (
                                      <div>
                                        <span className="fw-medium font-11">
                                          {" "}
                                          {product.imode === "SERVICE"
                                            ? "SAC:"
                                            : "HSN:"}
                                        </span>{" "}
                                        {product.hsnsac}
                                      </div>
                                    )}
                                  </td>
                                  <td className="force-style text-end">
                                    {product?.mrp}
                                  </td>
                                  <td className="force-style text-end">
                                    {product?.baseprice}
                                  </td>
                                  <td className="force-style text-center">
                                    {product?.quantity}
                                    <span className="font-11 ms-1">
                                      {(() => {
                                        const unitCode =
                                          product?.isSecondaryUnit
                                            ? product?.unitConfig
                                                ?.secondaryUnitCode
                                            : product?.unitConfig
                                                ?.primaryUnitCode;

                                        // return unitCode !== "unit" ? unitCode : "";
                                        return unitCode || "";
                                      })()}
                                    </span>
                                  </td>

                                  <td className="force-style text-end">
                                    {product?.lineGrandTotal}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="row mt-2">
                            <div className="col-6 pe-1">
                              <div className="font-12">
                                {documentConfig?.bankDetails && (
                                    <>
                                      <strong className="mb-1">
                                        Bank Details:
                                      </strong>
                                      <br />
                                   
                                    {documentConfig
                                          ?.bankDetails?.accountNo && (
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Account #:
                                        </span>
                                        {documentConfig
                                          ?.bankDetails?.accountNo || ""}
                                      </div>
                                     )}
                                     {documentConfig
                                          ?.bankDetails?.ifsc && ( 
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          IFSC:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.ifsc || ""}
                                      </div>
                                          )}   

                                           {documentConfig
                                          ?.bankDetails?.beneficiaryName && (
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Name:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.beneficiaryName || ""}
                                      </div>
                                          )}

                                           {documentConfig
                                          ?.bankDetails?.bankName && (
                                        <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Bank Name:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.bankName || ""}
                                      </div>
                                          )}
                                          {documentConfig?.bankDetails?.branch &&(
                                        <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Branch Name:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.branch || ""}
                                      </div>
                                          )}
                                          {documentConfig
                                          ?.upiId &&(
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          UPI ID:
                                        </span>

                                        {documentConfig
                                          ?.upiId || ""}
                                      </div>
                                          )}
                                    </>
                                  )}
                                <div className="mt-2 font-11">
                                  {`Total amount (in words) ${
                                    documentConfig?.amountInWords || ""
                                  }`}
                                </div>
                                {hasPayment && (
                                  <div className=" font-11">
                                    {parseAmount(documentConfig?.cashPaid) > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>{documentConfig.cashPaid}</b> Paid via
                                          Cash
                                        </span>
                                      </div>
                                    )}
                                    {parseAmount(documentConfig?.upiPaid) > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>{documentConfig.upiPaid}</b> Paid via
                                          UPI{" "}
                                        </span>
                                      </div>
                                    )}
                                    {parseAmount(documentConfig?.cardPaid) > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>{documentConfig.cardPaid}</b> Paid via
                                          Card{" "}
                                        </span>
                                      </div>
                                    )}
                                    {parseAmount(documentConfig?.onlinePaid) > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>{documentConfig.onlinePaid}</b> Paid
                                          via Online{" "}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* CENTER – QR CODE */}
                            <div className="col-2 pe-1 text-center">
                              {qrCodeDataUrl && (
                                  <img
                                    src={qrCodeDataUrl}
                                    alt="QR Code"
                                    style={{
                                      width: "90px",
                                      height: "90px",
                                    }}
                                  />
                                )}
                            </div>

                            <div className="col-4 pe-1">
                              <div className="font-11">
                                <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                  <span>Taxable Amount</span>
                                  <span>{documentConfig?.totalBasePrice} </span>
                                </div>
                                {isInterState ? (
                                  <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                    <span>IGST</span>
                                    <span>{documentConfig?.igstAmount} </span>
                                  </div>
                                ) : (
                                  <>
                                    <div className="d-flex justify-content-between   font-11 pe-sm-0-6">
                                      <span>CGST</span>
                                      <span>{documentConfig?.cgstAmount} </span>
                                    </div>

                                    <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                      <span>SGST</span>
                                      <span>{documentConfig?.sgstAmount} </span>
                                    </div>
                                  </>
                                )}
                                <div
                                  className="d-flex justify-content-between fw-bold mt-1 font-11 pe-sm-0-6"
                                  style={{
                                    backgroundColor:
                                      selectedTheme?.templateTheme
                                        ?.primaryColor, // light grey/blue
                                    padding: "4px 8px",

                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    borderRadius: "0px",
                                  }}
                                >
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    Total
                                  </span>
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    {documentConfig?.grandTotal}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between fw-bold font-11 pe-sm-0-6">
                                  <span>Amount Payable:</span>
                                  <span>{globalData?.balanceDue}</span>
                                </div>
                                <div className="d-flex justify-content-between font-11 pe-sm-0-6">
                                  <span>Amount paid:</span>
                                  <span>
                                    
                                    {documentConfig?.paidAmount}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {documentConfig?.remarks && (
                            <table width="100%" className="mb-2 mt-2">
                              <tbody>
                                <tr>
                                  <td width="100%">
                                    <div className="font-12 fw-600 align-left text-black mb-1">
                                      Remark
                                    </div>

                                    <div
                                      className="font-12 p-0"
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        minHeight: "30px",
                                      }}
                                    >
                                      {capitalizeFirstLetter(
                                        documentConfig?.remarks,
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                          <div className="row mt-2">
                            <div className="col-9 pe-1">
                              {terms?.length > 0  && !isPreSale && (
                                <table width="100%">
                                  <tbody>
                                    <tr>
                                      <td width="100%">
                                        <div
                                          className="fs-12 fw-600"
                                          style={{
                                            textAlign: "left",
                                            color: "black",
                                          }}
                                        >
                                          Terms & Conditions
                                        </div>
                                        <div className="terms-box font-10">
                                          {terms}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>
                            <div className="col-3 pe-2">
                              <div>
                                {storeSettings?.invoiceSettings?.signature && (
                                  <div className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings
                                          ?.signature
                                      }
                                      width={130}
                                    />
                                  </div>
                                )}
                                <div className="text-end p-0">
                                  Authorized Signatory
                                </div>
                                {/* <div className="text-end p-0 fw-bold font-14">
                                          {data?.salefrom?.title}
                                        </div> */}
                              </div>
                            </div>
                          </div>
                          {storeSettings?.negobillBranding && (
                            <div className="d-flex justify-content-center">
                              {" "}
                              <span>
                                Powered by <strong>NegoBill.com</strong>
                              </span>
                            </div>
                          )}

                          {/* <table
                            width="100%"
                            style={{ borderCollapse: "collapse" }}
                            cellPadding="8"
                          >
                            <tbody>
                            <tr>
                            {storeSettings?.invoiceSettings?.signature && (
                                  <td className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings?.signature
                                      }
                                      width={150}
                                    />
                                  </td> )}
          
                              </tr>
                              <tr>
                                <td className="text-end p-0">Issued By,Signature</td>
                              </tr>
                              <tr>
                                <td className="text-end p-0 fw-bold ">
                                  {settings?.saleProfileData?.title}
                                </td>
                              </tr>
                            </tbody>
                          </table> */}
                        </div>
                      </div>
                    </div>
                  </div>
             
              </>
            ) : showBillDetails ? (
              <>
               
                  <div className="tm_container">
                    <div className="tm_invoice_wrap">
                      <div className="tm_invoice tm_style1 tm_type1">
                        <div className="tm_invoice_in p-2">
                          <div className="d-flex justify-content-between mb-2">
                            <div className="tm_logo text-start">
                              {documentConfig?.image && (
                                <img
                                  src={
                                    documentConfig?.image
                                  }
                                  className="img-rounded logo mb-0"
                                  height="45"
                                  width="150"
                                  style={{ objectFit: "contain" }}
                                />
                              )}
                            </div>
                            <div className="text-end">
                              <div className="font-16">
                              {documentConfig?.title}
                                <div>
                                  <span className="font-12 fw-600">
                                   {documentConfig?.numberLabel}
                                  </span>
                                  {""}
                                  <strong className="font-12">
                                    {documentConfig?.number}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-6 pe-1 ">
                              <div className="d-flex gap-2 align-items-start">
                                <div>
                                  <div className="font-14 mb-1 fw-bold">
                                    {documentConfig?.fromData?.title || ""}
                                  </div>
                                  {documentConfig?.fromData?.gstnumber && (
                                    <div className="font-12 fw-600 mb-0">
                                      <>GSTIN:</>{" "}
                                      {documentConfig?.fromData?.gstnumber || ""}
                                    </div>
                                  )}
                                  <div className="font-11">
                                    {documentConfig?.fromData?.addressline1 || ""}{" "}
                                    {documentConfig?.fromData?.addressline2 || ""}{" "}
                                    {documentConfig?.fromData?.district || ""}
                                    {", "}
                                    {documentConfig?.fromData?.state || ""}
                                    {", "}
                                    {documentConfig?.fromData?.pincode || ""}
                                  </div>
                                  <div font-11>
                                    <strong>Mobile:</strong>{" "}
                                    {documentConfig?.fromData?.phone || ""}
                            {documentConfig?.fromData?.additionalPhone && (
                              <>, {documentConfig.fromData.additionalPhone}</>
                            )} |{" "}
                                    <strong>Email:</strong>{" "}
                                    {documentConfig?.fromData?.email || "-"}
                                  </div>
                                  {documentConfig?.fromData?.pannumber && (
                                    <div>
                                      PAN:{" "}
                                      {documentConfig?.fromData?.pannumber || ""}
                                    </div>
                                  )}
                                  {documentConfig?.fromData?.fssai && (
                                    <div>
                                      FASSI:{" "}
                                      {documentConfig?.fromData?.fssai || ""}
                                    </div>
                                  )}
                                  {documentConfig?.fromData?.cinnumber && (
                                    <div>
                                      CIN:{" "}
                                      {documentConfig?.fromData?.cinnumber}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row mt-2 mb-1">
                            <div className="col-4 pe-1">
                              {documentConfig?.toData && (
                                  <>
                                    <div>
                                      <div>
                                        <span className="fw-500">{documentConfig?.toLabel}</span>
                                        {""}
                                      </div>
                                      <div className="font-12 mb-0">
                                        <strong>
                                          {documentConfig?.toData?.name || ""}
                                        </strong>
                                      </div>
                                      {documentConfig?.toData?.gstnumber && (
                                        <div className="font-11">
                                          <strong>GSTIN:</strong>{" "}
                                          {documentConfig?.toData?.gstnumber || ""}
                                        </div>
                                      )}
                                      <div className="font-11">
                                        {documentConfig?.toData?.addressline1 || ""}{" "}
                                        {documentConfig?.toData?.addressline2 ||
                                          ""}{" "}
                                      </div>
                                      <div className="font-11">
                                        {documentConfig?.toData?.state || ""}{" "}
                                        {documentConfig?.toData?.district || ""}{" "}
                                        {documentConfig?.toData?.country || ""}{" "}
                                        {documentConfig?.toData?.pincode || ""}
                                      </div>
                                      {/* {documentConfig?.toData?.gstnumber && (
                                          <div className="font-11">
                                            <strong>GSTIN:</strong>{" "}
                                            {documentConfig?.toData?.gstnumber || ""}
                                          </div>
                                           )}  */}
                                      {documentConfig?.toData?.phone && (
                                        <div className="font-11">
                                          Ph: {documentConfig?.toData?.phone || ""}{" "}
                                          | Email:{" "}
                                          {documentConfig?.toData?.email || ""}
                                        </div>
                                      )}
                                      {/* <div> Email {documentConfig?.toData?.email || ""}</div> */}
                                      {documentConfig?.toData?.referenceid && (
                                        <div className="font-11">
                                          Reference No:{" "}
                                          {documentConfig?.toData?.referenceid || ""}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.pannumber && (
                                        <div className="font-11">
                                          PAN:{" "}
                                          {documentConfig?.toData?.pannumber || ""}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.fssai && (
                                        <div>
                                          FASSI:{" "}
                                          {documentConfig?.toData?.fssai || ""}
                                        </div>
                                      )}
                                      {documentConfig?.toData?.cinnumber && (
                                        <div className="font-11">
                                          CIN :{" "}
                                          {documentConfig?.toData?.cinnumber || ""}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                            </div>
                            {documentConfig?.toData?.shippingAddress &&
                              (documentConfig?.toData?.shippingAddress
                                ?.addressline1 ||
                                documentConfig?.toData?.shippingAddress
                                  ?.addressline2) && (
                                <>
                                  <div className="col-5 pe-1">
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        // fontWeight: "600",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Shipping Address
                                    </div>
                                    <div>
                                      {documentConfig?.toData?.shippingAddress
                                        ?.addressline1 || ""}{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.addressline2 || ""}{" "}
                                    </div>
                                    <div>
                                      {documentConfig?.toData?.shippingAddress
                                        ?.state || ""}{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.district || ""}{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.country || ""}{" "}
                                      -{" "}
                                      {documentConfig?.toData?.shippingAddress
                                        ?.pincode || ""}
                                    </div>
                                  </div>
                                </>
                              )}
                            <div className="col-3 ps-1 ms-auto">
                              {/* justify-content-between pushes the value to the right end */}
                              {documentConfig?.Date?.date && (
                                <>
                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">Bill Date:</span>
                                    <strong className="fw-semibold">
                                      {documentConfig?.Date?.date}
                                    </strong>
                                  </div>

                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">Bill Time:</span>
                                    <strong className="fw-semibold">
                                    {documentConfig?.Date?.time
                                      }
                                    </strong>
                                  </div>
                                </>
                              )}
                              {documentConfig?.toData?.state && (
                                <div className="d-flex justify-content-between mb-10 font-11">
                                  <span className="fw-bold">
                                    Place of Supply:
                                  </span>
                                  <strong className="fw-semibold text-end">
                                    {documentConfig?.toData?.state || ""}
                                  </strong>
                                </div>
                              )}
                            </div>
                          </div>

                          <table
                            // className="items"
                            width="100%"
                            style={{
                              marginTop: "2px",
                              borderCollapse: "collapse",
                              // borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              // borderBottom: "1px solid #000"
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr
                              //  style={{
                              //   borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              //   borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`
                              // }}
                              >
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "20px",
                                    textAlign: "center",
                                  }}
                                >
                                  #
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "auto",
                                    textAlign: "center",
                                  }}
                                >
                                  Item
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                  }}
                                >
                                  Selling Price
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "50px",
                                    textAlign: "center",
                                  }}
                                >
                                  Qty
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "center",
                                  }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {documentConfig?.items?.map((product, index) => (
                                <tr
                                  key={index}
                                  className="align-top font-11"
                                  style={{
                                    borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <td className="force-style align-left">
                                    {index + 1}
                                  </td>
                                  <td className="force-style align-left ">
                                    <div className="font-11">
                                      {product.title}
                                    </div>

                                    {product.hsnsac && (
                                      <div>
                                        <span className="fw-medium font-11">
                                          {" "}
                                          {product.imode === "SERVICE"
                                            ? "SAC:"
                                            : "HSN:"}
                                        </span>{" "}
                                        {product.hsnsac}
                                      </div>
                                    )}
                                  </td>

                                  <td className="force-style text-end">
                                    {product?.baseprice}
                                  </td>
                                  <td className="force-style text-center">
                                    {product?.quantity}
                                    <span className="font-11 ms-1">
                                      {(() => {
                                        const unitCode =
                                          product?.isSecondaryUnit
                                            ? product?.unitConfig
                                                ?.secondaryUnitCode
                                            : product?.unitConfig
                                                ?.primaryUnitCode;

                                        // return unitCode !== "unit" ? unitCode : "";
                                        return unitCode || "";
                                      })()}
                                    </span>
                                  </td>

                                  <td className="force-style text-end">
                                    {" "}
                                    {product?.lineGrandTotal}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="row mt-2">
                            <div className="col-6 pe-1">
                              <div className="font-12">
                                {documentConfig?.bankDetails && (
                                    <>
                                      <strong className="mb-1">
                                        Bank Details:
                                      </strong>
                                      <br />
                                   
                                    {documentConfig
                                          ?.bankDetails?.accountNo && (
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Account #:
                                        </span>
                                        {documentConfig
                                          ?.bankDetails?.accountNo || ""}
                                      </div>
                                     )}
                                     {documentConfig
                                          ?.bankDetails?.ifsc && ( 
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          IFSC:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.ifsc || ""}
                                      </div>
                                          )}   

                                           {documentConfig
                                          ?.bankDetails?.beneficiaryName && (
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Name:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.beneficiaryName || ""}
                                      </div>
                                          )}

                                           {documentConfig
                                          ?.bankDetails?.bankName && (
                                        <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Bank Name:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.bankName || ""}
                                      </div>
                                          )}
                                          {documentConfig?.bankDetails?.branch &&(
                                        <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Branch Name:
                                        </span>

                                        {documentConfig
                                          ?.bankDetails?.branch || ""}
                                      </div>
                                          )}
                                          {documentConfig
                                          ?.upiId &&(
                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          UPI ID:
                                        </span>

                                        {documentConfig
                                          ?.upiId || ""}
                                      </div>
                                          )}
                                    </>
                                  )}
                                <div className="mt-2 font-11">
                                  {`Total amount (in words) ${
                                    documentConfig?.amountInWords || ""
                                  }`}
                                </div>
                                {hasPayment && (
                                  <div className=" font-11">
                                    {parseAmount(documentConfig?.cashPaid) > 0 && (
                                      <div className=" font-10 ">
                                        <span>
                                          <b>{documentConfig.cashPaid}</b> Paid via
                                          Cash
                                        </span>
                                      </div>
                                    )}
                                    {parseAmount(documentConfig?.upiPaid) > 0 && (
                                      <div className=" font-10 ">
                                        <span>
                                          <b>{documentConfig.upiPaid}</b> Paid via
                                          UPI{" "}
                                        </span>
                                      </div>
                                    )}
                                    {parseAmount(documentConfig?.cardPaid) > 0 && (
                                      <div className=" font-10">
                                        <span>
                                          <b>{documentConfig.cardPaid}</b> Paid via
                                          Card{" "}
                                        </span>
                                      </div>
                                    )}
                                    {parseAmount(documentConfig?.onlinePaid) > 0 && (
                                      <div className=" font-10 ">
                                        <span>
                                          <b>{documentConfig.onlinePaid}</b> Paid
                                          via Online{" "}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* CENTER – QR CODE */}

                            <div className="col-2 pe-1 text-center">
                              {qrCodeDataUrl && (
                                  <img
                                    src={qrCodeDataUrl}
                                    alt="QR Code"
                                    style={{
                                      width: "90px",
                                      height: "90px",
                                    }}
                                  />
                                )}
                            </div>
                            <div className="col-4 pe-1">
                              <div className="font-11">
                                <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                  <span>Taxable Amount</span>
                                  <span>{documentConfig?.totalBasePrice} </span>
                                </div>

                                <div
                                  className="d-flex justify-content-between fw-bold mt-2 font-11 pe-sm-0-6 "
                                  style={{
                                    backgroundColor:
                                      selectedTheme?.templateTheme
                                        ?.primaryColor, // light grey/blue
                                    padding: "4px 8px",

                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    borderRadius: "0px",
                                  }}
                                >
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    Total
                                  </span>
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    {documentConfig?.grandTotal}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between fw-bold mt-1 font-11 pe-sm-0-6">
                                  <span>Amount Payable:</span>
                                  <span>{globalData?.balanceDue}</span>
                                </div>
                                <div className="d-flex justify-content-between font-11 pe-sm-0-6">
                                  <span>Amount paid:</span>
                                  <span>
                                    
                                    {documentConfig?.paidAmount}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {documentConfig?.remarks && (
                            <table width="100%" className="mb-2 mt-2">
                              <tbody>
                                <tr>
                                  <td width="100%">
                                    <div className="font-12 fw-600 align-left text-black mb-1">
                                      Remark
                                    </div>

                                    <div
                                      className="font-12 p-0"
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        minHeight: "30px",
                                      }}
                                    >
                                      {capitalizeFirstLetter(
                                        documentConfig?.remarks,
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                          <div className="row mt-2">
                            <div className="col-9 pe-1">
                              {terms?.length > 0  && !isPreSale && (
                                <table width="100%">
                                  <tbody>
                                    <tr>
                                      <td width="100%">
                                        <div
                                          className="fs-12 fw-600"
                                          style={{
                                            textAlign: "left",
                                            color: "black",
                                          }}
                                        >
                                          Terms & Conditions
                                        </div>
                                        <div className="terms-box font-10">
                                          {terms}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>
                            <div className="col-3 pe-2">
                              <div>
                                {storeSettings?.invoiceSettings?.signature && (
                                  <div className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings
                                          ?.signature
                                      }
                                      width={130}
                                    />
                                  </div>
                                )}
                                <div className="text-end p-0">
                                  Authorized Signatory
                                </div>
                                {/* <div className="text-end p-0 fw-bold font-14">
                                          {data?.salefrom?.title}
                                        </div> */}
                              </div>
                            </div>
                          </div>
                          {storeSettings?.negobillBranding && (
                            <div className="d-flex justify-content-center">
                              {" "}
                              <span>
                                Powered by <strong>NegoBill.com</strong>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
               
              </>
            ) : (
              <div className="default-content">
                <p>No content available for this sale mode.</p>
              </div>
            )}
                
    </>
  );
};

export default ServiceInvoice;
